/**
 * F11 · Intended load — its own screen, on EVERY order.
 *
 * MONEY BOUNDARY (unchanged, and the reason this file is deliberately dull):
 * `intended_load_amount_cents` is CAPTURED METADATA handed to the issuing bank.
 * It is never added to, and never read by, anything that computes a Cardmoji
 * charge. This screen writes it into the staged details object and nothing else;
 * the Stripe amount is resolved server-side from the card fee alone.
 *
 * U2 · the amounts stack VERTICALLY (one column, full width) — a horizontal row
 * of pills wrapped badly and read as a segmented control.
 * U3 · ONE explanatory block, below the amounts. Nothing above them but the H1.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S6_SECTION, S6_DEFAULTS } from "@/content/screens/s6";
import {
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  PrimaryButton,
  StickyBar,
} from "@/components/consumer/chrome";
import { cn } from "@/lib/utils";
import { readDetails, saveDetails } from "@/lib/consumer-session";
import { formatMoney, resolveLoadAmounts, resolveMaxLoadCents, resolvePricing } from "@/lib/consumer-config";
import { useConsumerFlow } from "../ConsumerFlow";

export default function S6bLoad() {
  const { t } = useSiteContent(S6_SECTION, S6_DEFAULTS);
  const { program } = useConsumerFlow();
  const navigate = useNavigate();

  const cfg = (program.config ?? {}) as Record<string, any>;
  // Pricing is read ONLY for the currency symbol. No amount here is chargeable.
  const pricing = useMemo(() => resolvePricing(cfg as any), [cfg]);
  /*
    NO TYPED DEFAULT. The pre-selected amount is the CONFIGURED single-load
    ceiling (`public_config.maxLoadCents`). A typed 100000 here happened to match
    config today and would have silently disagreed the moment config changed.
    Unconfigured = no default: the customer picks from the presets.
  */
  const maxLoadCents = useMemo(() => resolveMaxLoadCents(cfg), [cfg]);
  const options = useMemo(() => {
    const presets = resolveLoadAmounts(cfg);
    const all =
      maxLoadCents !== null && !presets.includes(maxLoadCents) ? [...presets, maxLoadCents] : presets;
    return [...all].sort((a, b) => a - b);
  }, [cfg, maxLoadCents]);

  const details = readDetails(program.slug);
  const [loadCents, setLoadCents] = useState<number>(
    details?.intended_load_amount_cents ?? maxLoadCents ?? options[0] ?? 0,
  );

  function onContinue() {
    // The ONLY write: the captured intent, staged with the rest of the order.
    saveDetails(program.slug, { ...(readDetails(program.slug) ?? {}), intended_load_amount_cents: loadCents } as any);
    navigate("../pay");
  }

  return (
    <FunnelPage testId="s6b-load">
      <FunnelContainer size="form">
        <FunnelHeader backTo="../address" backLabel={t("s6.header.backLabel")} />

        <Display className="mb-[14px] max-w-[20ch]">{t("s6.load.title")}</Display>

        {/*
          COUNSEL-OWNED, VERBATIM. Carries the money SEPARATION — the fee is
          Cardmoji's, the load is the customer's and moves through their bank.
          `s6.load.note` below describes funding only; it does not state the
          separation, which is why this sentence is stated here.
        */}
        <p className="mb-[22px] text-[14px] leading-[1.55] text-foreground/65" data-testid="s6-load-hint">
          {t("s6.load.hint")}
        </p>


        <div className="flex flex-col gap-2.5" data-testid="s6-load-amount">
          {options.map((cents) => {
            const active = loadCents === cents;
            return (
              <button
                key={cents}
                type="button"
                onClick={() => setLoadCents(cents)}
                data-testid={`s6-load-${cents}`}
                aria-pressed={active}
                className={cn(
                  "flex min-h-[56px] w-full items-center justify-between rounded-[16px] border px-5 text-[17px] font-semibold transition-colors",
                  active
                    ? "border-ink bg-ink text-paper"
                    : "border-foreground/[0.18] bg-white text-ink hover:border-ink",
                )}
              >
                <span>{formatMoney(cents, pricing.currency)}</span>
                {active ? <span className="text-[13px] font-semibold uppercase tracking-[0.08em]">Selected</span> : null}
              </button>
            );
          })}
        </div>

        {/* U3 · ONE block of copy, below the amounts. */}
        <p className="mt-5 text-[14px] leading-[1.55] text-foreground/65" data-testid="s6-load-note">
          {t("s6.load.note")}
        </p>
      </FunnelContainer>

      <StickyBar>
        <PrimaryButton onClick={onContinue} data-testid="s6-load-continue" className="basis-full">
          {t("s6.load.continue")}
        </PrimaryButton>
      </StickyBar>
    </FunnelPage>
  );
}
